import { useCallback, useEffect, useRef } from "react";
import { FetchQueryOptions, useQueryClient } from "@tanstack/react-query";

// 사이즈 보기 트리거에 hover 하는 순간 미리 당겨 둔다. 마켓 호출이 300~400ms 걸리는데
// hover 에서 click 까지 대개 그만큼 지나므로, 모달이 열릴 때는 이미 캐시에 있다.
//
// 다만 mouseenter 즉시 부르면 스크롤로 커서 밑을 스쳐 지나간 카드까지 전부 부른다 —
// 목록을 한 번 훑기만 해도 페이지의 상품 수만큼 마켓 호출이 나간다.
// 그래서 잠깐 머물러 있을 때만 부르고, 그전에 벗어나면 예약을 취소한다.
// 스쳐 지나가는 시간보다는 길고, 트리거를 누르려고 마음먹고 클릭하기까지보다는 짧게.
const HOVER_PREFETCH_DELAY = 500;

/**
 * hover 가 잠깐 이어질 때만 넘긴 쿼리들을 prefetch 한다.
 *
 * 크림·포이즌 옵션이 같은 지연·취소 규칙을 써야 해서(한쪽만 늦게 당기면 상세 모달이
 * 한 마켓만 채운 채로 열린다) 마켓별 훅에 복제하지 않고 여기 하나로 둔다.
 * queries 에 빈 배열/undefined 를 주면 예약 자체를 하지 않는다 — 목만 쓰는 화면은 이렇게 끈다.
 */
const useMarketOptionsHoverPrefetch = (
  queries: (FetchQueryOptions<any, any, any, any> | undefined)[]
) => {
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 예약이 걸린 뒤 렌더가 다시 돌아도 그 시점의 최신 쿼리로 당기게 한다
  // (deps 에 배열을 넣으면 매 렌더 새 참조라 핸들러 identity 가 계속 바뀐다).
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 페이지를 넘겨 카드가 사라지면 예약도 같이 버린다 — 이제 열릴 일이 없는 상품이다.
  useEffect(() => cancel, [cancel]);

  const onMouseEnter = useCallback(() => {
    cancel();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      queriesRef.current.forEach((query) => {
        if (query) {
          void queryClient.prefetchQuery(query);
        }
      });
    }, HOVER_PREFETCH_DELAY);
  }, [cancel, queryClient]);

  // hover 트리거가 여러 곳이면 그대로 펼쳐 쓴다(같은 상품이라 타이머 하나를 공유해도 된다).
  return { onMouseEnter, onMouseLeave: cancel };
};

export default useMarketOptionsHoverPrefetch;
